import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { formatCurrency } from "@/lib/utils";
import {
  Building2,
  Users,
  FileText,
  DollarSign,
  Key,
  Activity,
  TrendingUp,
  AlertCircle,
  RefreshCw,
  BarChart3,
} from "lucide-react";

function StatCard({
  title,
  value,
  sub,
  icon: Icon,
  color,
}: {
  title: string;
  value: string | number;
  sub?: string;
  icon: React.ElementType;
  color?: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className={`h-4 w-4 ${color || "text-muted-foreground"}`} />
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${color || ""}`}>{value}</div>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function SystemAnalytics() {
  const { data, isLoading, isError, refetch } = trpc.admin.systemSettings.analytics.useQuery();

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="space-y-6 animate-fade-in">
          <div>
            <Skeleton className="h-9 w-60 mb-2" />
            <Skeleton className="h-5 w-96" />
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full" />
            ))}
          </div>
          <Skeleton className="h-32 w-full" />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full" />
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (isError) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <AlertCircle className="h-12 w-12 text-destructive/40" />
          <p className="text-muted-foreground font-medium">Erreur de chargement des analytics</p>
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Réessayer
          </Button>
        </div>
      </AdminLayout>
    );
  }

  if (!data) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-2">
          <BarChart3 className="h-8 w-8 text-muted-foreground/30" />
          <p className="text-muted-foreground">Aucune donnée disponible</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Analytics Système</h1>
          <p className="text-muted-foreground">Métriques globales — entreprises, utilisateurs, revenus</p>
        </div>

        {/* Entreprises */}
        <div>
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Entreprises</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <StatCard title="Total entreprises" value={data.totalCompanies} icon={Building2} />
            <StatCard
              title="Actives"
              value={data.activeCompanies}
              sub={`${data.totalCompanies ? Math.round((data.activeCompanies / data.totalCompanies) * 100) : 0}% du total`}
              icon={Building2}
              color="text-success"
            />
            <StatCard title="En trial" value={data.trialCompanies} icon={AlertCircle} color="text-warning" />
            <StatCard title="Utilisateurs clients" value={data.totalUsers} icon={Users} />
          </div>
        </div>

        {/* Distribution des plans */}
        {Object.keys(data.planDistribution).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Distribution des plans</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                {Object.entries(data.planDistribution).map(([plan, count]) => (
                  <div key={plan} className="text-center">
                    <div className="text-2xl font-bold">{String(count)}</div>
                    <div className="text-sm text-muted-foreground capitalize">{plan}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* AO & Clés API */}
        <div>
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Appels d'offres & API
          </h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <StatCard title="Total AO ingérés" value={data.totalTenders} icon={FileText} />
            <StatCard title="AO ce mois-ci" value={data.tendersThisMonth} icon={TrendingUp} color="text-primary" />
            <StatCard title="Clés API actives" value={data.apiKeysActive} icon={Key} color="text-primary" />
            <StatCard
              title="Jobs (30j)"
              value={data.jobRunsLast30}
              sub={`${data.failedJobsLast30} échecs`}
              icon={Activity}
              color={data.failedJobsLast30 > 0 ? "text-destructive" : "text-success"}
            />
          </div>
        </div>

        {/* Revenue */}
        <div>
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Revenus (mois en cours)
          </h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <StatCard
              title="CA services"
              value={formatCurrency(data.serviceRevenueMonth)}
              icon={DollarSign}
              color="text-success"
            />
            <StatCard
              title="Success fees"
              value={formatCurrency(data.successFeeRevenueMonth)}
              icon={DollarSign}
              color="text-success"
            />
            <StatCard
              title="Total revenus"
              value={formatCurrency(data.totalRevenueMonth)}
              sub="Services + success fees"
              icon={TrendingUp}
              color="text-success"
            />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
